import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { getCiReportDemandSignals } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtDateShort, fmtRate, nightsBetween } from "../shared";
import { getDemandSignalTone, DEMAND_SIGNAL_TONE_CLASSES } from "../shared";

const PAGE_SIZE = 15;

const SignalBadge = ({ signal }) => {
  const cls = DEMAND_SIGNAL_TONE_CLASSES[getDemandSignalTone(signal)].badge;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold ${cls}`}>
      {signal || "—"}
    </span>
  );
};

const DemandSignalsBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No demand signal data for this client." />;
  }
  return (
    <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
      <table className="w-full min-w-[1100px] text-sm table-fixed responsive-card-table">
        <colgroup>
          <col style={{ width: "8%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "7%" }} />
        </colgroup>
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-3 py-2.5 text-left font-semibold">Date</th>
            <th className="px-3 py-2.5 text-left font-semibold">Window</th>
            <th className="px-3 py-2.5 text-right font-semibold">Your Rate</th>
            <th className="px-3 py-2.5 text-right font-semibold">Market Avg</th>
            <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Limited Avail.</th>
            <th className="px-3 py-2.5 text-right font-semibold">Min Stay</th>
            <th className="px-3 py-2.5 text-right font-semibold">Comp Occupancy</th>
            <th className="px-3 py-2.5 text-right font-semibold">Your Occupancy</th>
            <th className="px-3 py-2.5 text-left font-semibold">Signal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const highlight = (row.competitors_sold_out ?? 0) > 0;
            // Same date + window name can appear twice for different stay lengths
            // (e.g. the Sep 4 weekend as a 2-night and a 3-night window). The rows
            // are otherwise identical, so surface the nights to tell them apart —
            // matching the Rate Calendar, which shows nights only when > 1.
            const nights = nightsBetween(row.check_in, row.check_out);
            return (
              <tr
                key={`${row.check_in}-${row.check_out || ""}-${row.date_window_name || index}`}
                className={`border-b dark:border-gray-800 transition ${
                  highlight
                    ? "bg-emerald-50/60 dark:bg-emerald-900/10 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                    : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                }`}
              >
                <td data-label="Date" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                  {fmtDateShort(row.check_in)}
                </td>
                <td data-label="Window" className="px-3 py-2.5 text-gray-600 dark:text-gray-300 truncate" title={row.date_window_name || ""}>
                  {row.date_window_name || "—"}
                  {nights > 1 && (
                    <span className="text-gray-400 dark:text-gray-500"> · {nights} nights</span>
                  )}
                </td>
                <td data-label="Your Rate" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                  {fmtRate(row.your_rate)}
                </td>
                <td data-label="Market Avg" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {fmtRate(row.market_avg_rate)}
                </td>
                <td data-label="Limited Avail." className={`px-3 py-2.5 text-right whitespace-nowrap ${
                  highlight ? "font-semibold text-emerald-700 dark:text-emerald-300" : "text-gray-700 dark:text-gray-300"
                }`}>
                  <span>{row.competitors_sold_out ?? 0} / {row.total_competitors ?? 0}</span>
                </td>
                <td data-label="Min Stay" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {row.competitors_min_stay ?? 0}
                </td>
                <td data-label="Comp Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  <InferredOccupancy value={row.comp_avg_occupancy} />
                </td>
                <td data-label="Your Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  <InferredOccupancy value={row.your_occupancy} />
                </td>
                <td data-label="Signal" className="px-3 py-2.5 whitespace-nowrap">
                  <SignalBadge signal={row.demand_signal} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ThinScroll>
  );
};

const DemandSignalsBodyPaginated = ({ rows }) => {
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(rows, { pageSize: PAGE_SIZE });
  return (
    <>
      <DemandSignalsBody rows={pageItems} />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="rows"
          />
        </div>
      )}
    </>
  );
};

const DemandSignalsPanel = ({ clientId, say }) => (
  <AnalysisPanel
    id={`demand-signals-${clientId}`}
    title="Demand Signals"
    tip="Per-date demand read: how many competitors are at limited availability / under min-stay, plus their avg occupancy vs yours. NORMAL/RESTRICTED/HIGH DEMAND signal tells you whether the market is heating up for that date."
    description="Market demand indicators per date — limited-availability competitors, min-stay restrictions, and your rate vs market."
    fetcher={() => getCiReportDemandSignals(clientId)}
    deps={[clientId]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? data : (data?.items ?? []);
      // A1 — drop ghost rows from categories where the client has no inventory
      // (e.g. tent). The RPC always returns all categories (no p_category_code
      // param), so ghost rows have null your_rate. They produce misleading
      // HIGH DEMAND signals (1/1 competitors sold out) for categories the
      // client doesn't operate in. The filter always applies — there is no
      // tab distinction on this RPC.
      const filteredRows = rows.filter((row) => row.your_rate !== null);
      return (
        <>
          <PanelTakeaway panelKey="demand_signals" say={say} />
          <DemandSignalsBodyPaginated rows={filteredRows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default DemandSignalsPanel;
